import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CockpitShell, HouseFeedBadge } from "@/cockpit/CockpitShell";
import { ShadowMoneyCockpit } from "@/cockpit/shadow/ShadowMoneyCockpit";
import { mapShadowDoc, inLookback } from "@/cockpit/mapHouseFeed";
import { compactUsd } from "@/cockpit/formatCockpit";

const TYPES = [
  { id: "all", label: "All" },
  { id: "form4", label: "Form 4" },
  { id: "ptr", label: "PTR" },
  { id: "13f", label: "13F" },
  { id: "form144", label: "Form 144" },
];

const LOOKBACKS = [
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
  { id: 365, label: "1Y" },
];

function chipStyle(active) {
  return {
    color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
    background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
    borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
  };
}

export default function ShadowMoneyPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [group, setGroup] = useState("Portfolio");
  const [type, setType] = useState("all");
  const [lookback, setLookback] = useState(90);
  const [ticker, setTicker] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/shadow-money"),
      api.get("/scanner/groups").catch(() => ({ data: { groups: [] } })),
      api.get("/watchlist").catch(() => ({ data: { tickers: [] } })),
      api.get("/portfolio/positions").catch(() => ({ data: [] })),
    ]).then(([sm, g, wl, pos]) => {
      setRows(sm.data.rows || []);
      setLastRun(sm.data.last_run || null);
      setGroups(g.data.groups || []);
      setWatchlist((wl.data.tickers || []).map((t) => String(t).toUpperCase()));
      const held = [];
      const stocks = pos.data?.stocks || [];
      const opts = pos.data?.options || [];
      [...stocks, ...opts].forEach((p) => {
        const t = String(p.ticker || "").toUpperCase();
        if (t && !held.includes(t)) held.push(t);
      });
      setHoldings(held);
    }).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  const universe = useMemo(() => {
    if (group === "Portfolio") return new Set(holdings);
    if (group === "My Watchlist") return new Set(watchlist);
    const g = groups.find((x) => x.name === group);
    return g ? new Set((g.tickers || []).map((t) => String(t).toUpperCase())) : null;
  }, [group, holdings, watchlist, groups]);

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (universe && !universe.has(r.ticker)) return false;
      const ins = (r.insider?.latest || []).filter((tx) => inLookback(tx.date, lookback));
      const ptr = (r.congress || []).filter((c) => inLookback(c.date, lookback));
      const f144 = (r.proposed_sales || []).filter((s) => inLookback(s.date, lookback));
      const has13f = !!(r.institutions?.top_holders || []).length;
      if (type === "form4") return ins.length > 0 || (lookback <= 30 && ((r.insider?.buy_count_30d || 0) + (r.insider?.sell_count_30d || 0) > 0));
      if (type === "ptr") return ptr.length > 0;
      if (type === "13f") return has13f;
      if (type === "form144") return f144.length > 0;
      return ins.length || ptr.length || has13f || f144.length
        || (r.insider?.buy_count_30d || 0) || (r.insider?.sell_count_30d || 0);
    });
  }, [rows, universe, type, lookback]);

  useEffect(() => {
    if (!filtered.length) return;
    if (!filtered.some((r) => r.ticker === ticker)) setTicker(filtered[0].ticker);
  }, [filtered, ticker]);

  const selected = filtered.find((r) => r.ticker === ticker) || rows.find((r) => r.ticker === ticker);
  const data = useMemo(() => (selected ? { shadow: mapShadowDoc(selected) } : null), [selected]);

  return (
    <CockpitShell
      title="Shadow Money"
      blurb="Cached Form 4, congressional PTR, 13F snapshots, and Form 144 proposed sales from the FinancialData.net house feed (Mongo). Delayed — not live broker data. PTR amounts are disclosed ranges. Charts use that snapshot; they do not invent Q/Q 13F flow or ADV series the cache does not store."
      ticker={ticker}
      onTicker={setTicker}
      tickers={filtered.map((r) => r.ticker)}
      badge={<HouseFeedBadge />}
      lastRun={lastRun}
      testid="shadow-money-page"
      extraFilters={(
        <>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="shadow-group-chips">
            <span className="text-[10px] uppercase tracking-wide mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>Watchlists</span>
            {[{ name: "Portfolio" }, { name: "My Watchlist" }, ...groups].map((g) => (
              <button key={g.name} type="button" onClick={() => setGroup(g.name)}
                className="text-[11px] px-2 py-0.5 rounded-sm border"
                style={chipStyle(group === g.name)}>{g.name}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="shadow-type-chips">
            {TYPES.map((t) => (
              <button key={t.id} type="button" onClick={() => setType(t.id)}
                className="text-[11px] px-2 py-0.5 rounded-sm border"
                style={chipStyle(type === t.id)}>{t.label}</button>
            ))}
            <span className="ml-2" />
            {LOOKBACKS.map((l) => (
              <button key={l.id} type="button" onClick={() => setLookback(l.id)}
                className="text-[10px] px-2 py-0.5 rounded-sm border font-mono-data"
                style={chipStyle(lookback === l.id)}>{l.label}</button>
            ))}
          </div>
        </>
      )}
    >
      {loading ? (
        <div className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Loading filings…</div>
      ) : (
        <div className="border overflow-x-auto min-w-0" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          <table className="w-full text-[11px] font-mono-data" data-testid="shadow-money-table">
            <thead>
              <tr style={{ background: "var(--hoip-card)", color: "var(--hoip-text-tertiary)" }}>
                <th className="text-left px-2 py-1.5">Ticker</th>
                <th className="text-left px-2 py-1.5">Form 4</th>
                <th className="text-left px-2 py-1.5">PTR / Congress</th>
                <th className="text-left px-2 py-1.5">13F snapshot</th>
                <th className="text-left px-2 py-1.5">Form 144 proposed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const top = r.institutions?.top_holders?.[0];
                const buys = r.insider?.buy_count_30d || 0;
                const sells = r.insider?.sell_count_30d || 0;
                return (
                  <tr key={r.ticker}
                    className="border-t cursor-pointer hover:bg-[var(--hoip-hover)]"
                    style={{ borderColor: "var(--hoip-border)", background: ticker === r.ticker ? "var(--hoip-hover)" : undefined }}
                    onClick={() => setTicker(r.ticker)}
                    data-testid={`shadow-row-${r.ticker}`}>
                    <td className="px-2 py-1.5 font-bold">
                      <button type="button" className="underline decoration-dotted"
                        onClick={(e) => { e.stopPropagation(); navigate(`/ticker/${r.ticker}`); }}>
                        {r.ticker}
                      </button>
                    </td>
                    <td className="px-2 py-1.5">
                      <span style={{ color: "#10B981" }}>▲ {buys} buy</span>
                      {" / "}
                      <span style={{ color: "#EF4444" }}>▼ {sells} sell</span>
                    </td>
                    <td className="px-2 py-1.5">{(r.congress || []).length} PTR rows</td>
                    <td className="px-2 py-1.5">{top ? `${top.investor_name} · ${compactUsd(top.market_value)}` : "—"}</td>
                    <td className="px-2 py-1.5">{(r.proposed_sales || []).length} proposed</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-3 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>No cached filings for this filter.</div>
          )}
        </div>
      )}
      {data ? <ShadowMoneyCockpit data={data} /> : null}
      {ticker ? (
        <Link to={`/ticker/${ticker}`} className="text-blue-400 underline text-[10px]">Open ticker workbench</Link>
      ) : null}
    </CockpitShell>
  );
}
